import { useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ThumbsDown, ThumbsUp, Users } from "lucide-react";

import {
  CURATION_BATCH,
  useCurateEvent,
  useCurationQueue,
  useGoogleStatus,
  useSyncGoogle,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * Curation — the ninety days she already lived.
 *
 * This is where a founder who just connected her calendar lands. The argument
 * for putting it first: at that moment Omina can see three months of rooms and
 * knows nothing about what any of them meant, and that gap is the entire
 * reason its first recommendations would be guesses. Every card answered here
 * is a guess it no longer has to make.
 *
 * One event at a time, deliberately. The same information as a list would be a
 * backlog — twelve rows, each with three buttons, is a chore you postpone. One
 * card with one question is something you finish on a phone in a queue for
 * coffee, which is the only place this ever actually gets done.
 *
 * **The two-step shape is not arbitrary and should not grow.** It is fixed in
 * `packages/core/src/curation.ts`: how it went, then why. The cap is five
 * options and two steps, for the reason above. Both steps and every option on
 * screen come from the server with the queue — the same route validates writes
 * against them, so a chip that renders is by construction a chip the server
 * accepts.
 *
 * A meeting takes a different first question, and that is the point of asking
 * it at all: "did you like it?" about a client call is noise, and "what is
 * your relationship?" about a meetup has no answer. The detector suggests
 * which one, she can always disagree, and only her answer is written down.
 */
export const Route = createFileRoute("/curation")({
  component: Curation,
});

/**
 * What can be *asked about*. Deliberately narrower than `EventKind` in
 * @omina/core, which also has `personal` — that one is an answer, not a
 * question, so it never becomes the card's current kind and never reaches
 * `answerOne`. Keeping it out of this union is what makes that a type error
 * rather than a card that renders a question about a birthday.
 */
type Kind = "public" | "people_meeting";

/**
 * How far the card has to travel before letting go counts as an answer.
 *
 * 84px is roughly a thumb's width of deliberate movement: far enough that a
 * tap-with-a-wobble on a phone in one hand never commits a verdict she did not
 * mean, close enough that the throw stays one motion. Below it the card
 * springs home and nothing is written.
 */
const SWIPE_COMMIT = 84;

/** How loud the stamp is, given the throw so far. Full strength at the commit
 *  threshold, so reaching full opacity *is* the signal that letting go now
 *  will answer. */
function stampOpacity(dx: number | null, side: "left" | "right"): number {
  if (dx === null) return 0;
  const travel = side === "right" ? dx : -dx;
  if (travel <= 0) return 0;
  return Math.min(1, travel / SWIPE_COMMIT);
}

/** The mark grows into the throw, reaching full size exactly where letting go
 *  would answer. Starting at 0.6 rather than 0 means it arrives as a thumb
 *  rather than as a dot expanding out of nothing. */
function stampScale(dx: number | null, side: "left" | "right"): number {
  return 0.6 + 0.4 * stampOpacity(dx, side);
}

function Curation() {
  const queue = useCurationQueue();
  const google = useGoogleStatus();
  const curate = useCurateEvent();
  const sync = useSyncGoogle();
  const navigate = useNavigate();

  /**
   * What she has already answered, by id.
   *
   * Not an index into the queue, and that distinction is a bug I had to find
   * the hard way. Answering removes an event from the server's queue, so the
   * refetch returns a *shorter list* — advancing a cursor across it lands one
   * position further along a list that just shifted underneath, and every
   * second event is silently never shown. Filtering by id is immune to when
   * the refetch lands, and to it not landing at all.
   */
  const [answered, setAnswered] = useState<string[]>([]);
  const [kind, setKind] = useState<Kind | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  /**
   * Set only when she disagrees with the detector.
   *
   * It changes which first question is *asked*, and deliberately does not
   * answer it. The first version of this control flipped the kind and picked a
   * value in the same tap — "other" for a meeting, "neutral" for a room — so a
   * founder correcting a misdetected calendar entry silently stored a verdict
   * she had never given. That is a guess persisted next to a fact (AGENTS.md),
   * and three months later it is indistinguishable from something she said.
   */
  const [kindOverride, setKindOverride] = useState<Kind | null>(null);
  /** The denominator, frozen on arrival — see where it is set below. */
  const [total, setTotal] = useState<number | null>(null);
  /**
   * Whether she has seen the prep screen this sitting.
   *
   * The first card used to arrive cold: a founder one screen past consenting
   * to her calendar was suddenly looking at her own June with a swipe gesture
   * and no explanation of why Omina was asking. The screen before the first
   * card says what this step is, how short it is, and why it is worth doing —
   * which is the difference between judging four rooms and closing the tab.
   */
  const [started, setStarted] = useState(false);

  /**
   * The swipe.
   *
   * Pointer events rather than touch events, so one set of handlers covers a
   * finger, a trackpad and a mouse — and `setPointerCapture` keeps the drag
   * alive when the finger leaves the card, which is exactly what happens on
   * the throw that commits it.
   *
   * `dx` is the live horizontal offset and `null` means "not dragging"; the
   * card only animates back when it is null, so the spring never fights the
   * finger.
   */
  const [dx, setDx] = useState<number | null>(null);
  const startX = useRef(0);

  const connected = google.data?.scopes.calendar === true;
  const events = queue.data?.events ?? [];
  const pending = events.filter((e) => !answered.includes(e.id));
  /**
   * No card once the sitting is done.
   *
   * The navigation in `next` is the primary exit, but this is what makes the
   * limit true: a route change is a frame or two away, and without this the
   * next card renders in the meantime — which is exactly the "events like
   * crazy" behaviour, just briefer.
   */
  const finished = done >= CURATION_BATCH;
  const event = finished ? undefined : pending[0];

  /**
   * How many there were when she sat down, captured once.
   *
   * The queue is a rolling window of twenty, so answering one lets a
   * twenty-first in and the list length does not fall — the counter read "1 of
   * 20, 2 of 21, 3 of 22", climbing away from her as she worked, which is the
   * opposite of what a progress count is for. The server's own `pending`
   * total is the honest denominator, and freezing it on arrival also stops it
   * flickering while the refetch is in flight.
   */
  //
  // The denominator is the *sitting*, not the backlog: the queue asks for
  // `CURATION_BATCH` and the count has to agree with what she can actually
  // finish here, or "1 of 120" turns a one-minute step into a visible chore
  // she will not start. `pending` still bounds it, so a founder with three
  // events left is told three rather than six.
  if (total === null && queue.data) {
    setTotal(Math.min(queue.data.pending, CURATION_BATCH));
  }

  /** Reset the two-step state whenever we move to a new card. */
  function next(id: string) {
    setKind(null);
    setBranch(null);
    setKindOverride(null);
    setAnswered((a) => [...a, id]);
    setDone((n) => {
      const now = n + 1;
      /**
       * The sitting ends here, and it has to end *explicitly*.
       *
       * Asking the server for six does not bound anything: every answer
       * invalidates the queue, the refetch returns six *more* pending events
       * (the one just answered is no longer pending), and `answered` never
       * catches up because the replacements were never in it. The screen ran
       * forever while the counter sat at "of 6" — a limit that looked real and
       * was only the page size.
       *
       * So the count of what *she* has answered is the boundary, not the
       * length of the list.
       */
      if (now >= CURATION_BATCH) void navigate({ to: "/path" });
      return now;
    });
  }

  /**
   * Step 1. A public room takes a sentiment, a meeting takes a relationship,
   * and both open the same second question.
   */
  function answerOne(k: Kind, value: string) {
    setKind(k);
    setBranch(value);
  }

  /**
   * "Did not go" — and everything else with no verdict in it.
   *
   * `skipped` is the member of `SENTIMENTS` that ends the flow with no second
   * question, and it has to be one tap from the card because most of a
   * calendar is not networking. Ninety days of a real founder's calendar here
   * is `Pagar Nubank` nine times, `Call comercial` thirty-eight times and a
   * birthday. Without this, the queue asks her to rate paying a bill as a room
   * and she abandons the screen, which costs every verdict after it too.
   *
   * It is written down rather than merely hidden. A dismissal is an answer: it
   * stops the event being offered again, and it is what makes it safe to ask
   * about everything instead of guessing in advance what to filter out.
   */
  function dismiss() {
    if (!event) return;
    curate.mutate(
      {
        id: event.id,
        eventKind: "public",
        overallSentiment: "skipped",
        selectedOptions: [],
      },
      { onSuccess: () => next(event.id) },
    );
  }

  /**
   * "This was personal."
   *
   * A distinct answer from `dismiss`, not a synonym for it. Skipping says *no
   * verdict about this room*; this says *this was never a room*, which is the
   * only one of the two that means "stop asking me about things like this".
   * They were the same stored row until `personal` became a kind of its own —
   * see the type in @omina/core.
   *
   * Neither a sentiment nor a relationship: there is nothing to rate about a
   * birthday, and the card is finished in one tap.
   */
  function markPersonal() {
    if (!event) return;
    curate.mutate(
      { id: event.id, eventKind: "personal", selectedOptions: [] },
      { onSuccess: () => next(event.id) },
    );
  }

  /** Step 2 — and the write. */
  function answerTwo(optionId: string) {
    if (!event || !kind || !branch) return;
    curate.mutate(
      {
        id: event.id,
        eventKind: kind,
        overallSentiment: kind === "public" ? branch : null,
        relationshipType: kind === "people_meeting" ? branch : null,
        selectedOptions: [optionId],
      },
      // Advance on success only. Advancing optimistically and failing the
      // write would lose the answer silently, which is the one thing this
      // screen cannot do — she would have judged the evening for nothing.
      { onSuccess: () => next(event.id) },
    );
  }

  // ── the states that are not a card ──

  if (queue.isPending || google.isPending) {
    return <Shell><p className="lede">Reading your calendar…</p></Shell>;
  }

  /**
   * Not connected. An honest dead end with the way out on it, rather than an
   * empty queue that looks like "you have nothing to review" — those are
   * opposite facts and must not print the same screen (rule #2).
   */
  if (!connected) {
    return (
      <Shell>
        <div className="eyebrow">Curation</div>
        <h1 className="tk">Your calendar is not connected</h1>
        <p className="lede">
          This screen reviews the rooms you have already been in, so it needs the
          calendar you skipped on the way in. You can connect it, or just tell
          Omina what you are looking for instead.
        </p>
        <Link to="/interests" className="btn">
          Tell us your interests
        </Link>
        <Link to="/settings" className="authskip">
          Connect my calendar
        </Link>
      </Shell>
    );
  }

  /**
   * Connected, but nothing to show. Two very different reasons, and telling
   * them apart is the whole job: the sync may simply not have run yet (it is a
   * cron, and she arrived here seconds after consenting), or she may genuinely
   * have reviewed everything.
   */
  if (!event) {
    const neverSynced = !google.data?.calendar.lastSyncAt;

    if (neverSynced) {
      return (
        <Shell>
          <div className="eyebrow">Curation</div>
          <h1 className="tk">Fetching your calendar</h1>
          <p className="lede">
            Omina is reading the last ninety days. This takes a few seconds the
            first time.
          </p>
          <button
            type="button"
            className={cn("btn", sync.isPending && "loading")}
            disabled={sync.isPending}
            onClick={() => sync.mutate({ service: "calendar" })}
          >
            <span className="sp" aria-hidden />
            {sync.isPending ? "Reading…" : "Fetch it now"}
          </button>
          {sync.isError && <div className="danger">{sync.error.message}</div>}
        </Shell>
      );
    }

    return (
      <Shell>
        <div className="eyebrow">Curation</div>
        <h1 className="tk">{done ? "That is all of them" : "Nothing to review"}</h1>
        <p className="lede">
          {done
            ? `${done} ${done === 1 ? "room" : "rooms"} judged. Omina knows what to look for now.`
            : "There are no past events on your calendar waiting for a verdict."}
        </p>
        <Link to="/feed" className="btn">
          Find rooms
        </Link>
      </Shell>
    );
  }

  /**
   * The prep screen, before the first card.
   *
   * It has one job: make the ask legible before it is made. She is about to
   * hand over verdicts on her own calendar — that is personal, and asking for
   * it cold reads as surveillance. Said plainly first — what the step is, that
   * it is four cards, what the answers buy her — it reads as the setup it is.
   * The count is the frozen `total`, so the promise matches the counter she
   * is about to watch.
   */
  if (!started) {
    return (
      <Shell>
        <div className="eyebrow">Curation</div>
        <h1 className="tk">Now, what has been working</h1>
        <p className="lede">
          Your calendar holds the last ninety days of rooms. Omina will show you{" "}
          {total ?? CURATION_BATCH} of them, one at a time — two taps each to say
          what it was worth.
        </p>
        <p className="lede">
          This is the step that makes your matches yours. Every verdict teaches
          Omina what a good room looks like for you — and what to stop
          suggesting. Your answers stay between you and Omina.
        </p>
        <button type="button" className="btn" onClick={() => setStarted(true)}>
          Show me the first one
        </button>
      </Shell>
    );
  }

  // ── the card ──

  // `suggested` is `{ kind, reason }` — the reason is the detector explaining
  // itself, which is read-time only and never written down. Her correction
  // wins over it, and neither is stored until she answers.
  // The detector's own type allows `personal`, but it never suggests one and
  // this screen has no question for it, so anything but a meeting falls to the
  // room flow. She still has the explicit button.
  const detected = event.suggested?.kind;
  const suggested: Kind =
    kindOverride ?? (detected === "people_meeting" ? "people_meeting" : "public");
  const asking = kind !== null;

  /**
   * Only a room can be thrown.
   *
   * A call does not have a like and a dislike — "did you enjoy that client
   * call?" is not a question with an answer, which is the same reason the
   * thumbs never render for one. The swipe was the hole in that: it wrote
   * `eventKind: "public"` with a sentiment no matter what the card was, so
   * throwing a call filed it as a room she had rated. The verdict row said
   * one thing and the gesture did another.
   */
  const canSwipe = suggested === "public";
  const options = queue.data
    ? kind === "people_meeting"
      ? queue.data.options.meeting
      : branch && branch !== "skipped"
        ? (queue.data.options.public[branch as "liked" | "neutral" | "disliked"] ?? [])
        : []
    : [];

  return (
    <Shell>
      <div className="curhead">
        <div className="eyebrow">
          {done + 1} of {total ?? done + pending.length}
        </div>
        {/* The instruction has to match what the card actually does, or it is
            telling her to try a gesture that will not answer. */}
        <p className="hint curhint">
          {canSwipe ? "Swipe or tap. Two taps, then the next one." : "Who was this with?"}
        </p>
      </div>

      <div className="curstage">
        {/*
          The event itself. There is no image and there will not be one: a
          calendar entry carries a title, a time and a guest list and nothing
          else, and inventing a picture for it would be decoration standing in
          for information. So the title is the object — set large, in the
          display face, the way a poster would carry it.
        */}
        <div
          className={cn(
            "curcard",
            asking && "isblurred",
            dx !== null && "isdragging",
            canSwipe && "canswipe",
          )}
          aria-hidden={asking}
          style={
            dx === null
              ? undefined
              : {
                  // Rotation scaled off the throw, capped so a long drag tilts
                  // the card rather than spinning it.
                  transform: `translateX(${dx}px) rotate(${Math.max(-9, Math.min(9, dx * 0.05))}deg)`,
                }
          }
          onPointerDown={(e) => {
            // Not while the follow-up is open (the card is a backdrop then) and
            // not while a write is in flight.
            // A call has no verdict to throw at — see `canSwipe`.
            if (asking || curate.isPending || !canSwipe) return;
            // Capture keeps the drag alive once the finger leaves the card,
            // which is most of a real throw. It is an optimisation, not a
            // precondition: it throws outright if the pointer id is not
            // currently active, and letting that escape would abort the
            // handler *before* the drag was even initialised — the card then
            // ignores the gesture entirely rather than merely losing it at the
            // edge.
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* fall through to an uncaptured drag */
            }
            startX.current = e.clientX;
            setDx(0);
          }}
          onPointerMove={(e) => {
            if (dx === null) return;
            setDx(e.clientX - startX.current);
          }}
          onPointerUp={() => {
            if (dx === null) return;
            const thrown = dx;
            // Released, so the card animates home from wherever it was left.
            setDx(null);
            // Past the threshold this is the same answer as tapping the mark,
            // and it opens the same follow-up. The card springs back rather
            // than flying away on purpose: the next question is *about this
            // event*, and it has to still be there to be blurred behind it.
            //
            // Right is *dislike* and left is *like* — the opposite of Tinder,
            // and deliberately so. Throwing a room away to the right is the
            // gesture that matched her hand; borrowed convention lost to the
            // person actually using it.
            if (thrown > SWIPE_COMMIT) answerOne("public", "disliked");
            else if (thrown < -SWIPE_COMMIT) answerOne("public", "liked");
          }}
          onPointerCancel={() => setDx(null)}
        >
          {/*
            The choice, drawn large on the card as she throws it.

            This was a small stamped word in a box. It is the same thumb she
            would otherwise tap instead — bigger than anything else on the
            screen, growing as the throw does, so the answer is unmistakable
            before she lets go rather than something to read.

            Right is dislike, left is like, matching the commit above. Both are
            always mounted and one is always at zero opacity: mounting on
            demand would restart the scale transition mid-drag.
          */}
          <span
            className="curmark no"
            aria-hidden
            style={{
              opacity: stampOpacity(dx, "right"),
              transform: `scale(${stampScale(dx, "right")})`,
            }}
          >
            <ThumbsDown size={92} strokeWidth={1.5} fill="currentColor" />
          </span>
          <span
            className="curmark yes"
            aria-hidden
            style={{
              opacity: stampOpacity(dx, "left"),
              transform: `scale(${stampScale(dx, "left")})`,
            }}
          >
            <ThumbsUp size={92} strokeWidth={1.5} fill="currentColor" />
          </span>

          <p className="curwhen">{formatWhen(event.startsAt)}</p>
          <h1 className="curtitle">{event.title}</h1>
          {placeOf(event.location) && <p className="curwhere">{placeOf(event.location)}</p>}
          {event.attendees.length > 0 && (
            <p className="curwho">
              <Users size={13} aria-hidden />
              {event.attendees.length} {event.attendees.length === 1 ? "person" : "people"}
            </p>
          )}
        </div>

        {/*
          Step two, over the blurred card. An overlay rather than a new screen
          so the event stays visible behind the question — she is answering
          about *that* evening, and losing it while choosing why is how you get
          an answer to the wrong card.
        */}
        {asking && (
          <div className="curask">
            <p className="curasktitle">{askTitle(kind, branch)}</p>
            <ul className="curopts">
              {options.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className="curopt"
                    disabled={curate.isPending}
                    onClick={() => answerTwo(o.id)}
                  >
                    {o.label}
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="authskip"
              disabled={curate.isPending}
              onClick={() => {
                setKind(null);
                setBranch(null);
              }}
            >
              Back
            </button>
            {curate.isError && <div className="danger">{curate.error.message}</div>}
          </div>
        )}
      </div>

      {/* Step one. Hidden while the follow-up is open so there is exactly one
          question on screen at a time. */}
      {!asking &&
        (suggested === "people_meeting" ? (
          <MeetingStepOne
            busy={curate.isPending}
            relationships={queue.data?.options.relationships ?? []}
            onPick={(r) => answerOne("people_meeting", r)}
            onSkip={dismiss}
          />
        ) : (
          <SentimentStepOne
            busy={curate.isPending}
            onPick={(s) => answerOne("public", s)}
            onDidNotGo={dismiss}
          />
        ))}

      {/*
        The two corrections, both quiet: the detector is right most of the
        time, and neither of these is what she came here to do.

        "Personal, do not track" is a real third answer rather than a second
        way to skip — see `markPersonal`. It is the one that ends the card
        outright, because a birthday has nothing to rate and asking anyway is
        exactly the friction that gets a queue abandoned.
      */}
      {!asking && (
        <div className="curesc">
          <button
            type="button"
            className="authskip curswap"
            disabled={curate.isPending}
            onClick={() =>
              setKindOverride(suggested === "people_meeting" ? "public" : "people_meeting")
            }
          >
            {suggested === "people_meeting"
              ? "This was an event, not a meeting"
              : "This was a meeting, not an event"}
          </button>
          <button
            type="button"
            className="authskip curswap"
            disabled={curate.isPending}
            onClick={markPersonal}
          >
            Personal event, do not track
          </button>
        </div>
      )}
    </Shell>
  );
}

/**
 * The verdict: liked, did not go, disliked.
 *
 * The thumbs carry no label. They are the two most recognisable symbols in
 * software and a word under each was reading as instructions for something
 * obvious; the accessible name lives in `aria-label`, where it costs a
 * screen reader everything and a sighted founder nothing.
 *
 * **"Did not go" is not the old "so-so".** It replaced it, and the two are not
 * the same answer: `neutral` opens a follow-up asking what the room was like
 * ("content was average", "might go again if convenient"), and every one of
 * those is an answer from somebody who was there. So this writes `skipped`,
 * which is the member of `SENTIMENTS` that ends the flow with no second
 * question — the honest shape for an evening she has nothing to report about.
 *
 * The consequence, stated because it is invisible: `neutral` is no longer
 * reachable from this screen. `PUBLIC_EVENT_OPTIONS.neutral` still exists and
 * the API still accepts it, but nothing offers it. If a middle verdict is
 * wanted back, it needs its own control rather than this one.
 */
function SentimentStepOne({
  onPick,
  onDidNotGo,
  busy,
}: {
  onPick: (s: string) => void;
  onDidNotGo: () => void;
  busy: boolean;
}) {
  return (
    <div className="curverdict">
      {/* `fill="currentColor"` makes the thumb a solid shape rather than an
          outline, so the colour is the mark instead of a tint on its edge. */}
      <button
        type="button"
        className="curv yes"
        aria-label="Worth it"
        disabled={busy}
        onClick={() => onPick("liked")}
      >
        <ThumbsUp size={34} strokeWidth={1.5} fill="currentColor" aria-hidden />
      </button>
      <button type="button" className="curv curvtext" disabled={busy} onClick={onDidNotGo}>
        Did not go
      </button>
      <button
        type="button"
        className="curv no"
        aria-label="Not worth it"
        disabled={busy}
        onClick={() => onPick("disliked")}
      >
        <ThumbsDown size={34} strokeWidth={1.5} fill="currentColor" aria-hidden />
      </button>
    </div>
  );
}

/**
 * A meeting asks who, not how it felt.
 *
 * No thumbs, and the card behind this cannot be thrown either: a call has no
 * like and no dislike, so offering either would be asking a question with no
 * answer. It goes straight to the feedback — who the person is, then what came
 * out of it.
 *
 * `onSkip` is here because removing the thumbs also removed the only way past
 * a card. On a real calendar this is the difference between a screen that
 * finishes and one that does not: hers has `Call comercial` thirty-eight
 * times, and without an escape every one of them has to be classified before
 * the queue moves.
 */
function MeetingStepOne({
  relationships,
  onPick,
  onSkip,
  busy,
}: {
  relationships: readonly string[];
  onPick: (r: string) => void;
  onSkip: () => void;
  busy: boolean;
}) {
  return (
    <div className="curwhoask">
      <p className="curasktitle">Who was this with?</p>
      <ul className="curopts">
        {relationships.map((r) => (
          <li key={r}>
            <button
              type="button"
              className="curopt"
              disabled={busy}
              onClick={() => onPick(r)}
            >
              {RELATIONSHIP_LABELS[r] ?? r}
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="authskip" disabled={busy} onClick={onSkip}>
        Skip this one
      </button>
    </div>
  );
}

/** The ids are the storage format; these are what a person reads. */
const RELATIONSHIP_LABELS: Record<string, string> = {
  prospect: "A prospect",
  client: "A client",
  investor: "An investor",
  vendor_partner: "A vendor or partner",
  peer_friend: "A peer or friend",
  team: "My team",
  other: "Someone else",
};

function askTitle(kind: Kind | null, branch: string | null): string {
  if (kind === "people_meeting") return "What came out of it?";
  if (branch === "liked") return "What made it worth it?";
  if (branch === "disliked") return "What was wrong with it?";
  return "What was it like?";
}

/**
 * Where it was, in a form worth reading.
 *
 * Calendar `location` is free text and half of hers is a registration link —
 * one card showed `https://luma.com/event/evt-1UohnLhGKP8BvGK` across the
 * width of the phone, which is a line she cannot use to remember an evening
 * and is the widest thing on a screen whose subject is the title. A URL
 * collapses to its host, which is the only part that says anything ("luma.com"
 * means the online one), and everything else prints as written.
 */
function placeOf(location: string | null | undefined): string | null {
  const raw = location?.trim();
  if (!raw) return null;
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** "Tue 12 Aug" — enough to place the evening, nothing more. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="curwrap">{children}</div>;
}
